import React, { useState } from 'react';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { ChatMessageExportParams, downloadChatMessagesCSV } from '../../api/chatMessages';
import { useAuth } from '../../hooks/useAuth';

interface ChatMessageDownloadButtonProps {
  roomType: 'TALK_ROOM' | 'SENIOR_TALK_ROOM' | 'ADMIN_TALK_ROOM' | 'WORKSHOP_ROOM';
  roomId: number;
  size?: 'small' | 'medium' | 'large';
  variant?: 'text' | 'outlined' | 'contained';
  color?: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'error';
  buttonText?: string;
  iconOnly?: boolean;
  disabled?: boolean;
  className?: string;
  themeColor?: {
    main: string;
    hover: string;
  };
}

/**
 * チャットメッセージCSVダウンロードボタンコンポーネント
 */
export const ChatMessageDownloadButton: React.FC<ChatMessageDownloadButtonProps> = ({
  roomType,
  roomId,
  size = 'medium',
  variant = 'outlined',
  color = 'primary',
  buttonText = 'チャット ダウンロード(CSV)',
  iconOnly = false,
  disabled = false,
  className,
  themeColor,
}) => {
  const { user } = useAuth();
  const [ isDownloading, setIsDownloading ] = useState(false);

  // 管理者以外はボタンを表示しない
  if (user?.role !== 'ADMIN') {
    return null;
  }
  const handleDownload = async() => {
    if (isDownloading || disabled) return;
    try {
      setIsDownloading(true);
      const params: ChatMessageExportParams = {
        room_type: roomType,
        room_id: roomId,
      };
      await downloadChatMessagesCSV(params);
    } catch (error) {
      console.error('CSV download failed:', error);
      // エラー通知をここで表示することも可能
      // 例: setSnackbarMessage('CSVダウンロードに失敗しました');
    } finally {
      setIsDownloading(false);
    }
  };
  const buttonContent = (
    <Button
      variant={variant}
      color={color}
      size={size}
      onClick={handleDownload}
      disabled={disabled || isDownloading}
      startIcon={isDownloading ? <CircularProgress size={16}/> : <DownloadIcon/>}
      className={className}
      sx={themeColor ? {
        color: themeColor.main,
        borderColor: themeColor.main,
        '&:hover': {
          backgroundColor: themeColor.hover,
          borderColor: themeColor.main,
        },
      } : undefined}
    >
      {!iconOnly && (isDownloading ? 'ダウンロード中...' : buttonText)}
    </Button>
  );
  // アイコンのみの場合はTooltipでラップ
  if (iconOnly) {
    return (
      <Tooltip title={isDownloading ? 'ダウンロード中...' : buttonText}>
        <span>
          <Button
            variant={variant}
            color={color}
            size={size}
            onClick={handleDownload}
            disabled={disabled || isDownloading}
            className={className}
            sx={{
              minWidth: 'auto',
              padding: '3px',
              ...(themeColor ? {
                color: themeColor.main,
                borderColor: themeColor.main,
                '&:hover': {
                  backgroundColor: themeColor.hover,
                  borderColor: themeColor.main,
                },
              } : {})
            }}
          >
            {isDownloading ? <CircularProgress size={16}/> : <DownloadIcon/>}
          </Button>
        </span>
      </Tooltip>
    );
  }
  return buttonContent;
};
export default ChatMessageDownloadButton;
